import React from 'react';
import PropTypes from 'prop-types';

/**
 * ProgressBar component with Tailwind CSS — replaces react-bootstrap ProgressBar.
 *
 * Usage:
 *   <ProgressBar now={60} />
 *   <ProgressBar now={60} label variant="success" size="lg" />
 *   <ProgressBar now={30} max={60} label="30 of 60 files" />
 */
const VARIANT_CLASSES = {
  primary: 'tw-bg-blue-600',
  success: 'tw-bg-green-600',
  danger: 'tw-bg-red-600',
  warning: 'tw-bg-yellow-500',
  info: 'tw-bg-cyan-500',
};

const SIZE_CLASSES = {
  sm: 'tw-h-1.5',
  md: 'tw-h-2.5',
  lg: 'tw-h-4',
};

const ProgressBar = ({
  now = 0,
  max = 100,
  label = false,
  variant = 'primary',
  size = 'md',
  animated = false,
  className = '',
}) => {
  const safeMax = max > 0 ? max : 100;
  const clamped = Math.min(Math.max(now, 0), safeMax);
  const percent = Math.round((clamped / safeMax) * 100);
  const labelText = label === true ? `${percent}%` : label;

  return (
    <div className={`tw-w-full tw-bg-slate-200 tw-rounded-xl tw-overflow-hidden ${SIZE_CLASSES[size]} ${className}`}>
      <div
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        style={{ width: `${percent}%` }}
        className={`tw-h-full tw-rounded-xl tw-transition-all tw-duration-300 tw-flex tw-items-center tw-justify-center ${VARIANT_CLASSES[variant]} ${animated ? 'tw-animate-pulse' : ''}`}
      >
        {labelText && (
          <span className="tw-text-[10px] tw-leading-none tw-font-semibold tw-text-white tw-px-1.5 tw-whitespace-nowrap">
            {labelText}
          </span>
        )}
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  now: PropTypes.number,
  max: PropTypes.number,
  label: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  variant: PropTypes.oneOf(['primary', 'success', 'danger', 'warning', 'info']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  animated: PropTypes.bool,
  className: PropTypes.string,
};

export default ProgressBar;
